import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UsernameBadges, UsernameSize } from '@freelancer/components/username';
import type {
  CategoryPageViewUser,
  ProjectViewUser,
  SearchUser,
} from '@freelancer/datastore/collections';
import { FreelancerReputation } from '@freelancer/datastore/collections';
import { FlagSize } from '@freelancer/ui/flag';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { QueryParams } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';

// Exclusive use for My List.
@Component({
  standalone: false,
  selector: 'fl-user-info-my-list',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInfoMyListComponent {}

@Component({
  standalone: false,
  selector: 'fl-user-info',
  template: `
    <div class="UsernameWrapper">
      <fl-username
        flTrackingLabel="RedirectToProfile"
        flTrackingSection="SearchUsersResultCardDesktop"
        [class]="getDesktopMarginClass()"
        [badges]="userBadges"
        [link]="userLink"
        [queryParams]="userLinkQueryParams"
        [displayName]="isBidCard(user) ? user.displayName : user.publicName"
        [username]="user.username"
        [size]="usernameSize"
        [sizeDesktop]="usernameSizeDesktop"
        [truncateText]="true"
      ></fl-username>
      <div class="MyList">
        <ng-content select="fl-user-info-my-list"></ng-content>
      </div>
    </div>
    <div class="Container">
      <div class="Userinfo">
        @if (!isNewFreelancer || isBidder) {
          @if (isSearchCard(user) || isBidCard(user)) {
            <div
              class="mb-xxxsmall desktop:mb-none mr-xxsmall"
              [matTooltip]="ratingTooltipMessage"
              [matTooltipDisabled]="!showTooltip"
            >
              <fl-rating
                class="UserRating"
                [compact]="true"
                [compactDesktopSmall]="false"
                [control]="ratingControl"
                [halfRating]="true"
                [size]="IconSize.SMALL"
                [sizeDesktop]="IconSize.MID"
                [readOnly]="true"
              ></fl-rating>
            </div>
          }
          @if (hasReputation(user)) {
            <fl-review-count
              class="mb-xxxsmall desktop:mb-none mr-xxsmall"
              [matTooltip]="reviewTooltipMessage"
              [matTooltipDisabled]="!showTooltip"
              [counter]="
                isSearchCard(user)
                  ? user.reviews
                  : user.reputation?.entireHistory.reviews
              "
              [size]="IconSize.SMALL"
              [sizeDesktop]="IconSize.MID"
            ></fl-review-count>
          }
          <!--
          Hide earnings score for TKN projects, e.g. Deloitte internal and
          insource projects
          -->
          @if (showEarningScore && hasReputation(user) && !isTokenProject) {
            <fl-earnings
              class="mb-xxxsmall desktop:mb-none mr-xxsmall"
              [matTooltip]="earningTooltipMessage"
              [matTooltipDisabled]="!showTooltip"
              [compact]="true"
              [earnings]="
                isSearchCard(user)
                  ? freelancerReputation?.earningsScore
                  : user.reputation?.earningsScore
              "
              [size]="IconSize.SMALL"
              [sizeDesktop]="IconSize.MID"
            ></fl-earnings>
          }
          <!-- Hide completion rate on Deloitte internal projects -->
          @if (freelancerReputation && !(isDeloitteProject && isTokenProject)) {
            <fl-completed-jobs
              class="mb-xxxsmall desktop:mb-none mr-xxsmall"
              i18n-matTooltip="Jobs percentage tooltip message"
              matTooltip="Percentage of successfully completed jobs."
              [matTooltipDisabled]="!showTooltip"
              [completeCounter]="
                freelancerReputation?.entireHistory.completionRate
              "
              [size]="IconSize.SMALL"
              [sizeDesktop]="IconSize.MID"
            ></fl-completed-jobs>
          }
        }
        @if (isNewFreelancer && !isBidder && showNewFreelancerLabel) {
          <div class="NewFreelancer mb-xxxsmall desktop:mb-none mr-xxsmall">
            <fl-icon
              class="NewFreelancerIcon mr-xxxsmall"
              [color]="IconColor.INHERIT"
              [size]="IconSize.SMALL"
              [sizeDesktop]="IconSize.MID"
              [name]="'ui-star'"
              [useIconFont]="true"
            ></fl-icon>
            <p
              class="text-body-16 desktop:text-body-16"
              i18n="New Freelancer user"
            >
              New Freelancer
            </p>
          </div>
        }
        @if (showCountry && user.location) {
          <div class="UserLocation mb-xxxsmall desktop:mb-none">
            <fl-flag
              class="mr-xxsmall"
              [countryCode]="user.location?.country?.code"
              [size]="FlagSize.MID"
            ></fl-flag>
            <p
              class="text-body-16 desktop:text-body-16"
              i18n="Indicates city and country location of the user"
            >
              @if (showCity && isShortCityName) {
                {{ user.location?.city | titlecase }},
              }
              {{ user.location?.country?.name | titlecase }}
            </p>
          </div>
        }
      </div>
    </div>
  `,
  styleUrls: ['./user-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInfoComponent implements OnChanges, OnInit {
  FlagSize = FlagSize;
  IconColor = IconColor;
  IconSize = IconSize;

  @Input() freelancerReputation?: FreelancerReputation;
  @Input() isDeloitteProject?: boolean;
  @Input() isTokenProject?: boolean;
  @Input() ratingControl = new FormControl(0, { nonNullable: true });
  @Input() showCity? = true;
  @Input() showCountry? = true;
  @Input() showNewFreelancerLabel?: boolean = true;
  @Input() showTooltip = true;
  @Input() showEarningScore? = true;
  @Input() user: ProjectViewUser | SearchUser | CategoryPageViewUser;
  @Input() userBadges?: UsernameBadges;
  @Input() userLink?: string;
  @Input() userLinkQueryParams?: QueryParams;
  @Input() usernameMarginBottomDesktop: Margin = 'xxxsmall';
  @Input() usernameSize: UsernameSize;
  @Input() usernameSizeDesktop: UsernameSize;

  // To be used just in the bid-pattern-card to correct view of the card.yep
  @Input() isBidder? = true;

  ratingTooltipMessage = '';
  earningTooltipMessage = '';
  reviewTooltipMessage = $localize`Total number of reviews received.`;
  isShortCityName: boolean;
  readonly CITY_LENGTH_LIMIT = 10;
  isNewFreelancer = false;

  getDesktopMarginClass(): string {
    return `desktop:mb-${this.usernameMarginBottomDesktop}`;
  }

  ngOnInit(): void {
    if (this.isBidCard(this.user)) {
      this.isNewFreelancer = !!this.user.isNewFreelancer;
    }

    if (this.isSearchCard(this.user)) {
      this.isNewFreelancer = this.user.reviews === 0 && this.user.rating === 0;
      this.isBidder = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.showTooltip) {
      if (this.isBidCard(this.user)) {
        this.ratingTooltipMessage = $localize`Overall rating for projects with the same skills as this project`;
        this.earningTooltipMessage = $localize`Total amount earned by this freelancer in the skills of this project (out of 10.0).`;
      } else {
        this.ratingTooltipMessage = $localize`Overall rating of the user`;
        this.earningTooltipMessage = $localize`Earning score out of 10 for doing jobs in this skill or category. Increases as jobs are successfully completed and paid through the site.`;
      }
    } else {
      this.ratingTooltipMessage = '';
      this.earningTooltipMessage = '';
      this.reviewTooltipMessage = '';
    }

    if (this.isSearchCard(this.user) && this.user.location?.city) {
      if (this.user.location?.city.length >= this.CITY_LENGTH_LIMIT) {
        this.isShortCityName = false;
      } else {
        this.isShortCityName = true;
      }
    } else {
      this.isShortCityName = true;
    }
  }

  isBidCard(
    user: ProjectViewUser | SearchUser | CategoryPageViewUser,
  ): user is ProjectViewUser {
    return 'displayName' in user && 'employerReputation' in user;
  }

  isSearchCard(
    user: ProjectViewUser | SearchUser | CategoryPageViewUser,
  ): user is SearchUser {
    return 'reviews' in user;
  }

  hasReputation(
    user: ProjectViewUser | SearchUser | CategoryPageViewUser,
  ): user is ProjectViewUser | SearchUser {
    return 'reputation' in user;
  }
}
